import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import styles from './connects.module.scss';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import { Button } from '../Button';
import { Typography } from '../Typography';
import { Fade } from 'react-awesome-reveal';
import { BackgroundCircle } from '../BackgroundCircle';
import { TextInput } from '../TextInput';
import { Checkbox } from '../Checkbox';
import { trackFormSubmission } from '../../utils/analytics';

// TypeScript Interfaces
interface AboutData {
  _id: string;
  address: string;
  phone: string;
  aboutus: string;
  email?: string;
}

interface ConnectProps {
  aboutData: AboutData[];
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  file: File | null;
  privacy: boolean;
}

const Connect = ({ aboutData }: ConnectProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const route = useRouter();
  const id = route.query.id;

  const validationSchema = Yup.object({
    name: Yup.string().required('Enter a full name'),
    email: Yup.string()
      .email('Invalid email')
      .required('Enter an email address like example@mysite.com.'),
    phone: Yup.string().matches(/^[0-9+\-\s()]+$/, 'Please enter a valid phone number'),
    company: Yup.string().max(100, 'Company name is too long'),
    message: Yup.string().required('Enter an answer.'),
    privacy: Yup.boolean()
      .oneOf([true], 'You must agree to the privacy policy.')
      .required('You must agree to the privacy policy.'),
  });

  const initialValues: FormValues = {
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    file: null,
    privacy: false,
  };

  const handleSubmit = async (values: FormValues, { resetForm }: any): Promise<void> => {
    const formData = new FormData();
    formData.append('name', values.name);
    formData.append('email', values.email);
    if (values.phone) formData.append('phone', values.phone);
    if (values.company) formData.append('company', values.company);
    formData.append('message', values.message);

    if (values.file) {
      formData.append('file', values.file);
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1}/contacts`,
        formData
      );
      toast.success('Your message has been sent successfully.');

      // Track form submission for analytics
      trackFormSubmission('contact_form', '/');

      // Reset form and file state
      setFileName('');
      resetForm();
      setLoading(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Something went wrong. Please try again.'
      );
      setLoading(false);
    }
  };

  const handleContactClick = () => {
    window.open('/contact-us', '_blank');
  };

  const handleFAQClick = () => {
    window.open('/faq', '_blank');
  };

  useEffect(() => {
    if (id) {
      setTimeout(() => {
        const anchor = document.querySelector('#contact-form');
        if (anchor) {
          const elementPosition = anchor.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - 60;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
        route.replace('/', undefined, { shallow: true });
      }, 1000);
    }
  }, [id, route]);

  const handleFileDelete = (setFieldValue: (field: string, value: File | null) => void): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFieldValue('file', null);
  };

  return (
    <div className={styles.connectSection}>
      <div className={styles.container}>
        <BackgroundCircle
          top="13%"
          left="30%"
          width="400px"
          height="400px"
          animation="glow"
          style={{ zIndex: 0 }}
        />

        {/* Left Content */}
        <Fade triggerOnce duration={800}>
          <div className={styles.leftContent}>
            <div className={styles.badge}>
              <Image src="/connect-badge.svg" alt="badge" width={352} height={88} />
            </div>

            <Typography className={styles.subtitle}>Interested in our products?</Typography>

            <Typography className={styles.title}>Let&apos;s discuss your needs</Typography>

            <Typography className={styles.description}>
              Share your requirements, creative workflow, target hosts, and technical constraints.
              Our team will help you shape them into the right product configuration and chart the
              clearest path from concept to a production-ready solution.
            </Typography>

            <div className={styles.buttonContainer}>
              <Button
                onClick={handleContactClick}
                className={styles.contactButton}
                icon={
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.934 4.16671C11.7479 4.32551 12.496 4.72359 13.0823 5.30999C13.6687 5.89639 14.0668 6.64443 14.2256 7.45837M10.934 0.833374C12.625 1.02124 14.202 1.77852 15.4058 2.98088C16.6097 4.18324 17.369 5.75922 17.559 7.45004M7.74811 10.7193C6.7468 9.71795 5.95614 8.58575 5.37614 7.37773C5.32625 7.27382 5.30131 7.22187 5.28214 7.15613C5.21404 6.9225 5.26296 6.63562 5.40464 6.43776C5.4445 6.38208 5.49213 6.33445 5.58739 6.23919C5.87873 5.94785 6.0244 5.80218 6.11964 5.6557C6.4788 5.10329 6.4788 4.39114 6.11964 3.83873C6.0244 3.69225 5.87873 3.54658 5.58739 3.25524L5.425 3.09285C4.98213 2.64998 4.7607 2.42855 4.52288 2.30826C4.04991 2.06904 3.49136 2.06904 3.01839 2.30826C2.78057 2.42855 2.55914 2.64998 2.11627 3.09285L1.98491 3.22422C1.54356 3.66557 1.32288 3.88624 1.15434 4.18627C0.967324 4.51919 0.832855 5.03627 0.833992 5.41812C0.835015 5.76224 0.901769 5.99743 1.03527 6.4678C1.75275 8.99563 3.10648 11.3809 5.09646 13.3709C7.08644 15.3609 9.47175 16.7146 11.9996 17.4321C12.4699 17.5656 12.7051 17.6324 13.0493 17.6334C13.4311 17.6345 13.9482 17.5001 14.2811 17.313C14.5811 17.1445 14.8018 16.9238 15.2432 16.4825L15.3745 16.3511C15.8174 15.9082 16.0388 15.6868 16.1591 15.449C16.3983 14.976 16.3983 14.4175 16.1591 13.9445C16.0388 13.7067 15.8174 13.4852 15.3745 13.0424L15.2121 12.88C14.9208 12.5886 14.7751 12.443 14.6286 12.3477C14.0762 11.9886 13.3641 11.9886 12.8117 12.3477C12.6652 12.443 12.5195 12.5886 12.2282 12.88C12.1329 12.9752 12.0853 13.0229 12.0296 13.0627C11.8318 13.2044 11.5449 13.2533 11.3112 13.1852C11.2455 13.1661 11.1936 13.1411 11.0896 13.0912C9.88163 12.5112 8.74943 11.7206 7.74811 10.7193Z"
                      stroke="#D6BBFB"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                iconPosition="left"
              >
                Contact us
              </Button>

              <Button
                onClick={handleFAQClick}
                className={styles.faqButton}
                icon={
                  <svg
                    width="17"
                    height="19"
                    viewBox="0 0 17 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.90896 6.54673C6.10488 5.98979 6.49158 5.52016 7.00058 5.22101C7.50959 4.92187 8.10804 4.81251 8.68994 4.91233C9.27184 5.01214 9.79964 5.31467 10.1799 5.76634C10.5601 6.21801 10.7682 6.78967 10.7673 7.38007C10.7673 9.04673 8.26729 9.88007 8.26729 9.88007M8.33398 13.2134H8.34232M0.833984 5.66461V12.4289C0.833984 12.7144 0.833984 12.8572 0.876055 12.9845C0.913274 13.0972 0.974116 13.2006 1.05451 13.2878C1.14539 13.3864 1.27019 13.4557 1.51979 13.5944L7.68646 17.0203C7.9228 17.1516 8.04096 17.2173 8.16611 17.243C8.27687 17.2658 8.3911 17.2658 8.50186 17.243C8.627 17.2173 8.74517 17.1516 8.98151 17.0203L15.1482 13.5944C15.3978 13.4557 15.5226 13.3864 15.6135 13.2878C15.6939 13.2006 15.7547 13.0972 15.7919 12.9845C15.834 12.8572 15.834 12.7144 15.834 12.4289V5.66461C15.834 5.37908 15.834 5.23631 15.7919 5.10898C15.7547 4.99633 15.6939 4.89293 15.6135 4.80569C15.5226 4.70707 15.3978 4.63774 15.1482 4.49907L8.98151 1.07314C8.74517 0.941845 8.627 0.876196 8.50186 0.850459C8.3911 0.827679 8.27687 0.827679 8.16611 0.850459C8.04096 0.876196 7.9228 0.941845 7.68646 1.07314L1.51979 4.49907C1.27019 4.63774 1.14539 4.70707 1.05451 4.80569C0.974116 4.89293 0.913274 4.99633 0.876055 5.10898C0.833984 5.23631 0.833984 5.37908 0.833984 5.66461Z"
                      stroke="#D6BBFB"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                iconPosition="left"
              >
                Read FAQ
              </Button>
            </div>
          </div>
        </Fade>

        {/* Right Form */}
        <Fade triggerOnce duration={800} delay={200}>
          <div className={styles.rightContent} id="contact-form">
            <div className={styles.formContainer}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ errors, touched, setFieldValue, values, setFieldTouched }) => (
                  <Form className={styles.form}>
                    <div className={styles.formRowFullWidth}>
                      <TextInput
                        id="name"
                        name="name"
                        type="text"
                        label="Full name"
                        placeholder="Enter your full name"
                        fullWidth
                        required
                        value={values.name}
                        onChange={(e: any) => setFieldValue('name', e.target.value)}
                        onBlur={() => setFieldTouched('name', true)}
                        error={touched.name && errors.name ? errors.name : undefined}
                      />
                    </div>

                    <div className={styles.formRowFullWidth}>
                      <TextInput
                        id="email"
                        name="email"
                        type="email"
                        label="Email"
                        placeholder="Enter your email"
                        fullWidth
                        required
                        value={values.email}
                        onChange={(e: any) => setFieldValue('email', e.target.value)}
                        onBlur={() => setFieldTouched('email', true)}
                        inputType="email"
                        error={touched.email && errors.email ? errors.email : undefined}
                      />
                    </div>

                    <div className={styles.formRowFullWidth}>
                      <TextInput
                        id="phone"
                        name="phone"
                        type="tel"
                        label="Phone number"
                        placeholder="Enter your phone number"
                        fullWidth
                        inputType="phone"
                        value={values.phone}
                        onChange={(e: any) => setFieldValue('phone', e.target.value)}
                        onBlur={() => setFieldTouched('phone', true)}
                        error={touched.phone && errors.phone ? errors.phone : undefined}
                      />
                    </div>

                    <div className={styles.formRowFullWidth}>
                      <TextInput
                        id="company"
                        name="company"
                        type="text"
                        label="Company name"
                        placeholder="Enter your company name"
                        fullWidth
                        value={values.company}
                        onChange={(e: any) => setFieldValue('company', e.target.value)}
                        onBlur={() => setFieldTouched('company', true)}
                        error={touched.company && errors.company ? errors.company : undefined}
                      />
                    </div>

                    <div className={styles.formRowFullWidth}>
                      <TextInput
                        id="message"
                        name="message"
                        label="Message"
                        placeholder="Leave us a message..."
                        fullWidth
                        required
                        multiline
                        rows={4}
                        value={values.message}
                        onChange={(e: any) => setFieldValue('message', e.target.value)}
                        onBlur={() => setFieldTouched('message', true)}
                        error={touched.message && errors.message ? errors.message : undefined}
                      />
                    </div>

                    {fileName && (
                      <div className={styles.fileContainer}>
                        <div className={styles.fileInfo}>
                          <span className={styles.fileLabel}>Attached file:</span>
                          <span className={styles.fileName} title={fileName}>
                            {fileName}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleFileDelete(setFieldValue);
                          }}
                          aria-label={`Remove ${fileName}`}
                          className={styles.deleteBtn}
                        >
                          <Image src="/deleteicon.png" alt="Delete file" width={24} height={24} />
                        </button>
                      </div>
                    )}

                    <div className={styles.formRow}>
                      <Checkbox
                        id="privacy"
                        name="privacy"
                        label={
                          <>
                            You agree to our friendly{' '}
                            <Typography
                              variant="text-md"
                              as="span"
                              color="brand-500"
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => window.open('/privacy-policy', '_blank')}
                            >
                              privacy policy
                            </Typography>
                          </>
                        }
                        required
                        checked={values.privacy}
                        onChange={(checked: boolean) => setFieldValue('privacy', checked)}
                        onBlur={() => setFieldTouched('privacy', true)}
                        error={touched.privacy && errors.privacy ? errors.privacy : undefined}
                      />
                    </div>

                    <div className={styles.buttonRow}>
                      <Button
                        type="submit"
                        fullWidth
                        disabled={loading}
                        label={loading ? 'Sending...' : 'Send message'}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        fullWidth
                        onClick={() => {
                          fileInputRef.current?.click();
                        }}
                        icon={
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M8 12L8 4M4 8L8 4L12 8"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                        iconPosition="right"
                        label="Upload File"
                      />

                      <input
                        type="file"
                        accept="image/*, application/pdf, .doc, .docx"
                        className={styles.filess}
                        ref={fileInputRef}
                        onChange={(e) => {
                          if (e.currentTarget.files) {
                            setFileName(e.currentTarget.files[0]?.name || '');
                            setFieldValue('file', e.currentTarget.files[0]);
                          }
                        }}
                        style={{ display: 'none' }}
                        aria-label="File input"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Fade>
      </div>
    </div>
  );
};

export default Connect;
